import React, { useState, useEffect } from 'react';
import type { Supplement, SupplementCatalog } from '../models/diary';
import { diaryService } from '../services/diaryService';
import SearchableSelect from './SearchableSelect';

interface SupplementItemFormProps {
  supplements: Supplement[];
  patientUserId?: string | number;
  onSupplementsChange: (supplements: Supplement[]) => void;
  onNameChange: (name: string) => void;
  isEditMode?: boolean;
  onDelete?: () => void;
}

const SupplementItemForm: React.FC<SupplementItemFormProps> = ({
  supplements,
  patientUserId,
  onSupplementsChange,
  onNameChange,
  isEditMode = false,
  onDelete
}) => {
  const [supplementCatalog, setSupplementCatalog] = useState<SupplementCatalog[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  // Fetch supplement catalog when component mounts
  useEffect(() => {
    if (patientUserId) {
      const fetchCatalog = async () => {
        setLoadingCatalogs(true);
        try {
          const suppCatalog = await diaryService.getSupplementCatalog();
          setSupplementCatalog(suppCatalog);
        } catch (error) {
          console.error('Error fetching supplement catalog:', error);
        } finally {
          setLoadingCatalogs(false);
        }
      };

      fetchCatalog();
    }
  }, [patientUserId]);

  // Initialize with one empty supplement if needed
  useEffect(() => {
    if (supplements.length === 0) {
      onSupplementsChange([{ description: '' } as Supplement]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get the single supplement (we always work with index 0)
  const supplement = supplements[0] || { description: '' };

  const handleCatalogChange = (catalogId: number) => {
    const catalog = supplementCatalog.find(c => c.id === catalogId);
    if (catalog) {
      const updatedSupplement: Supplement = {
        ...supplement,
        supplementCatalog: catalog
      };
      onSupplementsChange([updatedSupplement]);
      onNameChange(catalog.name);
    }
  };

  const handleDescriptionChange = (description: string) => {
    const updatedSupplement: Supplement = {
      ...supplement,
      description
    };
    onSupplementsChange([updatedSupplement]);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Section Header */}
      <div style={{ 
        marginBottom: 16, 
        paddingBottom: 12, 
        borderBottom: '2px solid #e5e7eb' 
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: 16, 
              fontWeight: 600, 
              color: '#1f2937' 
            }}>
              Suplemento
            </h3>
            <p style={{ 
              margin: '4px 0 0 0', 
              fontSize: 13, 
              color: '#6b7280' 
            }}>
              Selecciona el suplemento del catálogo y define las instrucciones de dosificación
            </p>
          </div>
          {isEditMode && onDelete && (
            <button 
              type="button"
              onClick={onDelete}
              style={{ 
                background: '#fee2e2', 
                color: '#991b1b', 
                border: 'none', 
                borderRadius: 6, 
                padding: '8px 16px', 
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#fecaca'}
              onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}
            >
              <span>🗑️</span>
              <span>Eliminar</span>
            </button>
          )}
        </div>
      </div>

      {/* Catalog Selection */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block' }}>
          <span style={{ 
            fontSize: 13, 
            fontWeight: 600, 
            color: '#374151',
            marginBottom: 8,
            display: 'block'
          }}>
            1. Suplemento del catálogo *
          </span>
          {loadingCatalogs ? (
            <div style={{ 
              padding: 12, 
              fontSize: 13, 
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: 6,
              border: '1px dashed #d1d5db',
              textAlign: 'center'
            }}>
              Cargando catálogo...
            </div>
          ) : (
            <SearchableSelect
              options={supplementCatalog.map(catalog => ({
                value: catalog.id,
                label: catalog.name,
                searchText: `${catalog.name} ${catalog.description || ''}`
              }))}
              value={supplement.supplementCatalog?.id || ''}
              onChange={(value) => handleCatalogChange(Number(value))}
              placeholder="-- Selecciona un suplemento --"
              hasError={!supplement.supplementCatalog?.id}
            />
          )}
        </label>
      </div>
      
      {/* Catalog Info Display */}
      {supplement.supplementCatalog && (
        <div style={{ 
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 6,
          padding: 12,
          marginBottom: 16
        }}>
          <div style={{ 
            fontSize: 12, 
            fontWeight: 600,
            color: '#1e40af',
            marginBottom: 6
          }}>
            ℹ️ Información del suplemento
          </div>
          {supplement.supplementCatalog.description && (
            <div style={{ 
              fontSize: 12, 
              color: '#374151',
              marginBottom: 8,
              lineHeight: '1.5'
            }}>
              {supplement.supplementCatalog.description.length > 200 
                ? `${supplement.supplementCatalog.description.substring(0, 200)}...` 
                : supplement.supplementCatalog.description}
            </div>
          )}
          {supplement.supplementCatalog.purchaseUrl && (
            <a 
              href={supplement.supplementCatalog.purchaseUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ 
                fontSize: 12,
                color: '#38b2ac',
                textDecoration: 'none',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              🔗 Ver enlace de compra →
            </a>
          )}
        </div>
      )}
      
      {/* Dosage Instructions */}
      <div>
        <label style={{ display: 'block' }}>
          <span style={{ 
            fontSize: 13, 
            fontWeight: 600, 
            color: '#374151',
            marginBottom: 6,
            display: 'block'
          }}>
            2. Instrucciones de dosificación *
          </span>
          <span style={{
            fontSize: 12,
            color: '#6b7280',
            marginBottom: 8,
            display: 'block'
          }}>
            Especifica cómo y cuándo debe tomar el suplemento
          </span>
          <textarea 
            value={supplement.description || ''} 
            onChange={e => handleDescriptionChange(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '10px 12px', 
              border: supplement.description ? '1px solid #d1d5db' : '2px solid #fbbf24',
              borderRadius: 6, 
              fontSize: 13, 
              minHeight: 70, 
              resize: 'vertical',
              fontFamily: 'inherit',
              outline: 'none',
              boxSizing: 'border-box'
            }} 
            placeholder="Ej: 1 cápsula al día por la mañana con el desayuno (10,000 UI)"
          />
        </label>
      </div>
    </div>
  );
};

export default SupplementItemForm;
